import { useEffect, useRef, useState } from "react";
import { X, Music2, Sparkles, Play, Download, AlertCircle, ImageIcon, Loader2, Share2, Check, Link2 } from "lucide-react";
import { api, API_BASE, APP_ORIGIN } from "@/lib/api";
import { toast } from "sonner";

/**
 * KaraokeModal — Studio Karaoke render UI. Full lifecycle in one modal:
 *   1. Pick a video-background style (gradient · waveform · custom)
 *   2. Optionally paste/upload a background image (custom style only)
 *   3. Hit "Generate karaoke" → backend orchestrates LALAL.AI vocals split
 *      → Whisper word-level transcription → FFmpeg karaoke MP4 render
 *   4. Modal shows per-step progress ("Splitting vocals…" → "Transcribing…"
 *      → "Rendering karaoke video…") via polling
 *   5. On success shows an inline <video> preview + a Download button
 *   6. NEW (#123): "Share to socials" panel with per-platform toggles
 *      and a single "Post now" button that queues via the existing
 *      dispatcher_loop → publishes to TikTok, Reels, Shorts, etc.
 *
 * Props:
 *   open           bool            — visibility
 *   asset          {asset_id, label, name} — source music track
 *   onClose()      —
 *   onCreated(job) — optional callback fired when the render completes
 */
const PLATFORM_LABELS = {
  tiktok:    { name: "TikTok",    accent: "#25F4EE" },
  instagram: { name: "Reels",     accent: "#E1306C" },
  youtube:   { name: "Shorts",    accent: "#FF0000" },
  facebook:  { name: "Facebook",  accent: "#1877F2" },
  x:         { name: "X",         accent: "#a9b3c9" },
  threads:   { name: "Threads",   accent: "#a78bfa" },
};
const STYLES = [
  {
    id: "gradient",
    icon: Sparkles,
    label: "Gradient",
    hint: "Dark amber → cyan gradient. Fastest render, cleanest look.",
    accent: "#FF8A1F",
  },
  {
    id: "waveform",
    icon: Music2,
    label: "Waveform",
    hint: "Live audio-reactive waveform underneath the lyrics.",
    accent: "#4a9eff",
  },
  {
    id: "custom",
    icon: ImageIcon,
    label: "Custom image",
    hint: "Use your own background image with subtle Ken Burns zoom.",
    accent: "#a78bfa",
  },
];

const STEP_LABELS = {
  queued:         "Queued…",
  vocals_split:   "Splitting vocals via LALAL.AI…",
  compress_vocals: "Preparing vocals for transcription…",
  transcribe:    "Transcribing lyrics word-by-word…",
  build_subs:    "Building karaoke subtitles…",
  render_mp4:    "Rendering karaoke video…",
  done:          "Done!",
  failed:        "Failed",
};

export default function KaraokeModal({ open, asset, onClose, onCreated, defaultBackgroundImageUrl }) {
  const [style, setStyle] = useState("gradient");
  const [customUrl, setCustomUrl] = useState("");
  const [job, setJob] = useState(null);      // {job_id, status, progress_step, video_url, error, ...}
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  // ── Share-to-socials state (Update #123) ────────────────────────
  const [shareStatus, setShareStatus] = useState(null);   // {supported, connected, not_connected, latest_posts}
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [caption, setCaption] = useState("");
  const [sharing, setSharing] = useState(false);

  // Reset state whenever the modal opens on a new asset. If a
  // `defaultBackgroundImageUrl` was passed in (e.g. deep-linked from
  // Creative Studio), auto-pick the custom style + prefill the URL.
  useEffect(() => {
    if (open) {
      setStyle(defaultBackgroundImageUrl ? "custom" : "gradient");
      setCustomUrl(defaultBackgroundImageUrl || "");
      setJob(null);
      setBusy(false);
      setShareStatus(null);
      setSelectedPlatforms(new Set());
      setCaption("");
      setSharing(false);
    }
    return () => {
      if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    };
  }, [open, asset?.asset_id, defaultBackgroundImageUrl]);

  // Esc to close (but not during rendering — makes accidental close painful)
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose, busy]);

  const startJob = async () => {
    if (!asset?.asset_id) return;
    if (style === "custom" && !customUrl.trim()) {
      toast.error("Please provide a background image URL for the custom style");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/production/karaoke", {
        asset_id: asset.asset_id,
        style,
        background_image_url: style === "custom" ? customUrl.trim() : undefined,
      });
      const d = r.data || {};
      setJob({ ...d });
      // Kick off polling
      const poll = async (jobId) => {
        try {
          const s = await api.get(`/production/karaoke/${jobId}`);
          const j = s.data || {};
          setJob((prev) => ({ ...(prev || {}), ...j }));
          if (j.status === "succeeded") {
            setBusy(false);
            toast.success("Karaoke video ready!");
            if (onCreated) onCreated(j);
            // Kick off share-status fetch so the share panel is ready immediately
            try {
              const s2 = await api.get(`/production/karaoke/${jobId}/share-status`);
              setShareStatus(s2.data || null);
              // Pre-select all connected platforms so a single "Post now" click works
              const conn = new Set(s2.data?.connected || []);
              setSelectedPlatforms(conn);
              // Pre-fill a friendly caption; user can edit before posting
              const trackLabel = asset?.label || asset?.name || "a fresh track";
              setCaption(`🎤 Karaoke edit — ${trackLabel} 🎧\n\n#Karaoke #Radio #BroadcastFlow`);
            } catch { /* share panel just stays hidden */ }
            return;
          }
          if (j.status === "failed") {
            setBusy(false);
            toast.error(`Karaoke render failed: ${j.error || "unknown error"}`);
            return;
          }
          // Still working — schedule the next poll
          pollRef.current = setTimeout(() => poll(jobId), 3000);
        } catch (e) {
          setBusy(false);
          toast.error("Lost connection to the karaoke job — please retry.");
        }
      };
      pollRef.current = setTimeout(() => poll(d.job_id), 2500);
    } catch (e) {
      setBusy(false);
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.error === "byok_required") {
          toast.error(detail.message || "LALAL.AI license required", {
            duration: 7000,
            action: { label: "Connect", onClick: () => window.open("/dashboard/providers", "_blank") },
          });
        } else if (detail.error === "karaoke_locked") {
          toast.error(detail.message, {
            duration: 7000,
            action: { label: "See plans", onClick: () => window.open("/#pricing", "_blank") },
          });
        } else {
          toast.error(detail.message || "Karaoke render failed to start");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Karaoke render failed to start");
      }
    }
  };

  const togglePlatform = (pl) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(pl)) next.delete(pl); else next.add(pl);
      return next;
    });
  };

  const postToSocials = async () => {
    if (!job?.job_id) return;
    const platforms = Array.from(selectedPlatforms);
    if (platforms.length === 0) {
      toast.error("Pick at least one platform first");
      return;
    }
    setSharing(true);
    try {
      const r = await api.post(`/production/karaoke/${job.job_id}/share`, {
        platforms,
        caption,
      });
      const d = r.data || {};
      // Refresh share-status so the pills show up-to-date state
      try {
        const s2 = await api.get(`/production/karaoke/${job.job_id}/share-status`);
        setShareStatus(s2.data || null);
      } catch { /* noop */ }

      const queued = d.queued || [];
      const skipped = d.skipped || [];
      if (queued.length > 0) {
        toast.success(`Queued ${queued.length} post${queued.length === 1 ? "" : "s"} — publisher runs on the next tick`);
      }
      if (skipped.length > 0) {
        const dupCount = skipped.filter((s) => s.reason === "recent_duplicate").length;
        const ncCount = skipped.filter((s) => s.reason === "not_connected").length;
        const bits = [];
        if (dupCount > 0) bits.push(`${dupCount} already queued in the last 10 min`);
        if (ncCount > 0) bits.push(`${ncCount} not connected`);
        if (bits.length > 0) toast.message(`Skipped: ${bits.join(" · ")}`);
      }
      if (queued.length === 0 && skipped.length === 0) {
        toast.message("Nothing to queue — pick a connected platform first");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Share failed"));
    } finally {
      setSharing(false);
    }
  };

  if (!open) return null;

  const step = job?.progress_step;
  const stepLabel = STEP_LABELS[step] || (job?.status ? `Status: ${job.status}` : "Preparing…");
  const succeeded = job?.status === "succeeded" && job?.video_url;
  const failed = job?.status === "failed";
  const inProgress = job && !succeeded && !failed;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm"
      onClick={() => { if (!busy) onClose(); }}
      data-testid="karaoke-modal-backdrop"
    >
      <div
        className="relative w-full max-w-2xl bg-[#0f1117] border border-[#2a2f3a] rounded-lg shadow-2xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="karaoke-modal"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1 text-[#a9b3c9] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Close"
          data-testid="karaoke-modal-close"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Music2 size={18} className="text-[#FF8A1F]" />
          <div className="type-section-heading text-white">Studio Karaoke</div>
        </div>
        <div className="type-list-subtitle text-[#a9b3c9] mb-5 truncate">
          Track: <span className="text-white/80">{asset?.label || asset?.name || "Untitled"}</span>
        </div>

        {/* ── Progress state ─────────────────────────────────────── */}
        {inProgress && (
          <div className="mb-5 p-4 rounded-md border border-[#FF8A1F]/30 bg-[#FF8A1F]/5" data-testid="karaoke-progress">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 size={14} className="animate-spin text-[#FF8A1F]" />
              <div className="type-list-title text-white">{stepLabel}</div>
            </div>
            <div className="type-list-subtitle text-[#a9b3c9]">
              Total pipeline typically takes 45-120 s depending on track length.
              Please keep this window open.
            </div>
          </div>
        )}

        {/* ── Success state ──────────────────────────────────────── */}
        {succeeded && (
          <div className="mb-5" data-testid="karaoke-success">
            <video
              controls
              src={`${job.video_url.startsWith("/api") ? APP_ORIGIN : API_BASE}${job.video_url}`}
              className="w-full rounded-md border border-[#2a2f3a] bg-black"
              data-testid="karaoke-video"
            />
            <div className="flex items-center gap-2 mt-3">
              <a
                href={`${job.video_url.startsWith("/api") ? APP_ORIGIN : API_BASE}${job.video_url}?download=1`}
                download={`karaoke-${asset?.asset_id || "track"}.mp4`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FF8A1F] text-black type-button-label hover:opacity-90 transition"
                data-testid="karaoke-download"
              >
                <Download size={13} /> Download MP4
              </a>
              <div className="type-status-badge text-[#3ecf8e]">
                {job.language ? `Language: ${job.language.toUpperCase()}` : ""}
                {job.duration_s ? ` · ${Math.round(job.duration_s)}s` : ""}
              </div>
            </div>

            {/* ── Share to socials panel (Update #123) ────────── */}
            {shareStatus && (
              <div className="mt-5 pt-4 border-t border-[#2a2f3a]" data-testid="karaoke-share-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 size={13} className="text-[#4a9eff]" />
                  <div className="type-list-title text-white">Share to socials</div>
                </div>

                {/* Platform toggles */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {shareStatus.supported.map((pl) => {
                    const meta = PLATFORM_LABELS[pl] || { name: pl, accent: "#a9b3c9" };
                    const connected = shareStatus.connected.includes(pl);
                    const active = selectedPlatforms.has(pl);
                    const latest = shareStatus.latest_posts?.[pl];
                    return (
                      <button
                        key={pl}
                        type="button"
                        disabled={!connected || sharing}
                        onClick={() => togglePlatform(pl)}
                        className="text-left px-3 py-2 rounded-md border transition disabled:opacity-40 disabled:cursor-not-allowed bg-[#171a24]"
                        style={{
                          borderColor: active ? meta.accent : "#2a2f3a",
                          boxShadow: active ? `0 0 0 1px ${meta.accent} inset` : "none",
                        }}
                        data-testid={`karaoke-share-toggle-${pl}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="type-list-title text-white text-sm">{meta.name}</div>
                          {active && <Check size={11} style={{ color: meta.accent }} />}
                        </div>
                        <div className="type-status-badge mt-1" style={{ color: connected ? "#3ecf8e" : "#7a8399" }}>
                          {connected ? (latest?.status || "Ready") : "Not connected"}
                        </div>
                        {latest?.external_url && (
                          <a
                            href={latest.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-1 type-status-badge text-[#4a9eff] hover:underline"
                          >
                            <Link2 size={9} /> View post
                          </a>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Caption editor */}
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={3}
                  maxLength={2200}
                  disabled={sharing}
                  placeholder="Write a caption for your karaoke post…"
                  className="w-full px-3 py-2 bg-[#171a24] border border-[#2a2f3a] rounded-md text-white type-body focus:border-[#4a9eff] focus:outline-none resize-none disabled:opacity-50"
                  data-testid="karaoke-share-caption"
                />
                <div className="flex items-center justify-between mt-2">
                  <div className="type-list-subtitle text-[#7a8399]">
                    {caption.length}/2200 characters
                    {shareStatus.not_connected.length > 0 && (
                      <>
                        {" "}·{" "}
                        <a href="/dashboard/socials" target="_blank" rel="noopener noreferrer" className="text-[#4a9eff] hover:underline">
                          Connect more platforms
                        </a>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={postToSocials}
                    disabled={sharing || selectedPlatforms.size === 0}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#4a9eff] text-black type-button-label hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="karaoke-share-post"
                  >
                    <Share2 size={13} />
                    {sharing ? "Queuing…" : `Post to ${selectedPlatforms.size} ${selectedPlatforms.size === 1 ? "platform" : "platforms"}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Failure state ──────────────────────────────────────── */}
        {failed && (
          <div className="mb-5 p-4 rounded-md border border-red-500/40 bg-red-500/10" data-testid="karaoke-error">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={14} className="text-red-400" />
              <div className="type-list-title text-white">Render failed</div>
            </div>
            <div className="type-list-subtitle text-[#e0a0a0] break-words">
              {job?.error || "Unknown error"}
            </div>
          </div>
        )}

        {/* ── Style picker (only shown when no job is running) ──── */}
        {!inProgress && !succeeded && (
          <>
            <div className="type-list-title text-white mb-3">Video background style</div>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {STYLES.map((s) => {
                const Icon = s.icon;
                const active = style === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStyle(s.id)}
                    disabled={busy}
                    className="text-left p-3 rounded-md border transition disabled:opacity-50 bg-[#171a24] hover:bg-[#1a1f2e]"
                    style={{
                      borderColor: active ? s.accent : "#2a2f3a",
                      boxShadow: active ? `0 0 0 1px ${s.accent} inset` : "none",
                    }}
                    data-testid={`karaoke-style-${s.id}`}
                  >
                    <Icon size={14} style={{ color: s.accent }} className="mb-2" />
                    <div className="type-list-title text-white mb-1">{s.label}</div>
                    <div className="type-list-subtitle text-[#a9b3c9] leading-snug">{s.hint}</div>
                  </button>
                );
              })}
            </div>

            {style === "custom" && (
              <div className="mb-5">
                <label className="type-list-subtitle text-[#a9b3c9] block mb-1.5">
                  Background image URL
                </label>
                <input
                  type="text"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://…/image.jpg  or  /api/creative-studio/…"
                  className="w-full px-3 py-2 bg-[#171a24] border border-[#2a2f3a] rounded-md text-white type-body focus:border-[#a78bfa] focus:outline-none"
                  data-testid="karaoke-custom-url"
                />
                <div className="type-list-subtitle text-[#7a8399] mt-1.5">
                  Accepts absolute URLs, `/api/…` paths from Creative Studio,
                  or `data:image/…;base64,…` inline images.
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Action row ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#2a2f3a]">
          <div className="type-list-subtitle text-[#7a8399] max-w-[65%] leading-snug">
            {inProgress
              ? "Rendering — this may take up to 2 min for a full song."
              : succeeded
              ? "Karaoke ready. Share the MP4 anywhere."
              : "LALAL.AI splits vocals · Whisper transcribes · FFmpeg burns in synced lyrics."}
          </div>
          <div className="flex items-center gap-2">
            {succeeded || failed ? (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md bg-[#171a24] border border-[#2a2f3a] text-white type-button-label hover:bg-[#1a1f2e] transition"
                data-testid="karaoke-modal-done"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#a9b3c9] hover:text-white transition disabled:opacity-40"
                  data-testid="karaoke-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={startJob}
                  disabled={busy}
                  className="px-4 py-2 rounded-md bg-[#FF8A1F] text-black type-button-label hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="karaoke-modal-generate"
                >
                  {busy ? "Generating…" : "Generate karaoke →"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
